import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { careerPerks, openRoles, company } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CheckCircle2, Briefcase, MapPin } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { applicationsApi, track } from "../api";
import useSeo from "../hooks/useSeo";

const Careers = () => {
  useSeo({
    title: "Caregiver Jobs in Columbus, Ohio | STNA, HHA & PCA | Caregivers Alliance Homecare",
    description: "Join Caregivers Alliance Homecare in Columbus, Ohio. Caregiver jobs (STNA, home health aide, personal care aide, companion caregiver) with fair wages, paid training and career growth. Apply today.",
    keywords: "caregiver jobs Columbus Ohio, STNA jobs Ohio, home health aide jobs Ohio, personal care aide jobs Columbus, caregiver hiring Ohio",
    path: "/careers",
  });
  const { toast } = useToast();
  const location = useLocation();
  const countyParam = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("county") || "";
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : "";
  }, [location.search]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await applicationsApi.create(countyParam ? { ...form, message: `[${countyParam} County] ${form.message}` } : form);
      toast({ title: "Application received!", description: "Thanks for applying. Our team will reach out soon." });
      track("caregiver_application", countyParam);
      setForm({ name: "", email: "", phone: "", role: "", message: "" });
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or call (614) 526-1548.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <h1 className="font-display font-extrabold text-4xl md:text-5xl">
            {countyParam ? `Caregivers Needed in ${countyParam} County` : "Join Our Caregiver Team"}
          </h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            {countyParam ? `We're growing our caregiver team to serve families in ${countyParam} County. ${company.mission}` : company.mission}
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-12">
          <div>
            <span className="text-[#2E9E6A] font-semibold uppercase tracking-wide text-sm">Why Work With Us</span>
            <h2 className="font-display font-bold text-3xl text-[#0E2A33] mt-2">A career, not just a job</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {careerPerks.map((p) => (
                <div key={p} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#2E9E6A] mt-0.5 shrink-0" />
                  <span className="text-slate-700">{p}</span>
                </div>
              ))}
            </div>

            <h3 className="font-display font-bold text-xl text-[#0E2A33] mt-10 mb-4">Open Positions</h3>
            <div className="space-y-3">
              {openRoles.map((r) => (
                <div key={r.title} className="card-hover bg-[#F7FBFC] border border-slate-100 rounded-xl p-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-semibold text-[#0E2A33] flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-[#0E5A72]" /> {r.title}
                    </div>
                    <div className="text-sm text-slate-500 flex items-center gap-3 mt-1">
                      <span>{r.type}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.location}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 h-fit">
            <h3 className="font-display font-bold text-xl text-[#0E2A33]">Apply Now</h3>
            <p className="text-slate-500 text-sm mt-1">Tell us about yourself and we'll be in touch.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="c-name">Full name</Label>
                <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Jane Doe" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="jane@email.com" />
                </div>
                <div>
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="(614) 555-0100" />
                </div>
              </div>
              <div>
                <Label htmlFor="c-role">Role of interest</Label>
                <Input id="c-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1" placeholder="Home Health Aide" />
              </div>
              <div>
                <Label htmlFor="c-msg">Message</Label>
                <Textarea id="c-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" rows={4} placeholder="Tell us about your experience..." />
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-12">
                {busy ? "Submitting..." : "Submit Application"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default Careers;
